import { AsyncPipe, NgIf } from '@angular/common';
import { AfterViewInit, ChangeDetectionStrategy, Component, ElementRef, Input, NgZone, OnDestroy, ViewChild } from '@angular/core';
import { MatLegacyDialog } from '@angular/material/legacy-dialog';
import { MatLegacyTooltipModule } from '@angular/material/legacy-tooltip';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { Actions, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import WebViewer, { Core, WebViewerInstance } from '@pdftron/webviewer';
import { ToastrService } from 'ngx-toastr';
import { BehaviorSubject, firstValueFrom, merge, Observable, of, Subject, Subscription, switchMap } from 'rxjs';
import { distinctUntilChanged, filter, map, take, takeUntil } from 'rxjs/operators';
import { environment } from '../../../../environments/environment';
import { FileEntity } from '../../../dave-data-module/entities/file.entity';
import { VersionEntity, VersionEntityFromFileBackend } from '../../../dave-data-module/entities/version.entity';
import { FileDataService } from '../../../dave-data-module/services/file-data.service';
import { HttpService } from '../../../dave-data-module/services/http.service';
import { BaseActionTypes } from '../../../dave-data-module/State/actions/base.actions';
import { FilesActionTypes, FileUploadParams, fileUploadParamsObj } from '../../../dave-data-module/State/actions/files.actions';
import { getToken } from '../../../dave-data-module/State/selectors/base.selectors';
import { getSetting, getUser } from '../../../dave-data-module/State/selectors/users.selectors';
import { DaveLoadingModule } from '../../../dave-loading/dave-loading.module';
import { AppButtonModule } from '../../../dave-utils-module/app-button-module/app-button.module';
import { isNotNullOrUndefined } from '../../../helper/helper';
import { LoadingService } from '../../../services/loading.service';
import { PdfToolsService } from '../../../services/pdf-tools.service';
import { PdfEditableDialogComponent, PDFEditableDialogData, PDFEditableDialogReturnData } from '../pdf-editor-legacy/pdf-editable-dialog/pdf-editable-dialog.component';
import { RemarkPopupComponent, RemarkPopupComponentDialogData } from '../pdf-editor-legacy/remark-popup/remark-popup.component';

@Component({
    selector: 'app-pdf-editor',
    standalone: true,
    templateUrl: './pdf-editor.component.html',
    styleUrls: ['./pdf-editor.component.scss'],
    imports: [AppButtonModule, AsyncPipe, FontAwesomeModule, MatLegacyTooltipModule, NgIf, DaveLoadingModule],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PdfEditorComponent implements AfterViewInit, OnDestroy {
    @Input() Version: VersionEntity;
    @Input() SigningMode = false;
    private setCheckedButtonDocumentId$ = new BehaviorSubject<number | null>(null);
    @Input() set SetCheckedButtonDocumentId(id: number) {
        if (id !== this.setCheckedButtonDocumentId$.value) {
            this.setCheckedButtonDocumentId$.next(id);
        }
    }

    public resize() {
        this.instance.Core.documentViewer.updateView();
        this.instance.Core.documentViewer.refreshAll();
    }

    get SetCheckedButtonDocumentId() {
        return this.setCheckedButtonDocumentId$.value;
    }
    protected CheckedFile$: Observable<FileEntity>;

    private token$ = this.store.select(getToken).pipe(filter(isNotNullOrUndefined));

    public Editing = false;
    public SaveButton = false;
    private destroy$ = new Subject<void>();
    private instance: WebViewerInstance;
    private rubberStampTool: Core.Tools.RubberStampCreateTool;
    public ServiceGetElements = this.api.GetUrl('pdfviewer/GetElements', 'sfdt');
    public ServiceUploadNewVersionFromImages = this.api.GetUrl('pdfviewer/UploadNewVersionFromImages', 'sfdt');

    public multiStampModeStamp: Core.Annotations.StampAnnotation;
    public multiStampModeActive = false;

    @ViewChild('viewer') viewer!: ElementRef;

    private darkModeSub: Subscription;
    private userSub: Subscription;
    protected isLoading$ = new BehaviorSubject(false);

    private _dirty = false;
    get dirty() {
        return this._dirty;
    }
    constructor(
        private store: Store,
        private matDialog: MatLegacyDialog,
        public LS: LoadingService,
        private fs: FileDataService,
        private toastrService: ToastrService,
        private actions$: Actions,
        private api: HttpService,
        private pts: PdfToolsService,
        private ngZone: NgZone,
    ) {
        this.CheckedFile$ = this.setCheckedButtonDocumentId$.pipe(switchMap((id) => (id ? this.fs.GetFileById$(id).pipe(map((file) => file)) : of(null))));

        this.CheckedFile$.pipe(
            takeUntil(this.destroy$),
            distinctUntilChanged((a, b) => a?.Id === b?.Id),
        ).subscribe((f) => {
            if (f && isNotNullOrUndefined(f.Remark) && f.Remark != '') {
                const openedToasts = this.toastrService.toasts;
                for (const toast of openedToasts) {
                    if (toast.toastRef.componentInstance.toastPackage.toastType === 'toast-warning') {
                        this.toastrService.clear(toast.toastId);
                    }
                }
                this.toastrService.warning(f.Remark, 'Bemerkung:', { messageClass: 'font-bold' });
            }
        });
    }

    public OpenSignaturePopup() {
        console.log("TESTOpen")
        this.instance.UI.openElements(['signatureModal']);
    }

    public toggleAddCheckAnnotations() {
        if (this.multiStampModeActive) {
            this.instance.UI.enableElements([ 'rubberStampPanel'])
            this.stopAddCheckAnnotations();
        } else {
            this.instance.UI.disableElements([ 'rubberStampPanel'])
            this.startAddCheckAnnotations();
        }

    }

    public async startAddCheckAnnotations() {
        const defaultStamps: Core.Annotations.StampAnnotation[] = await this.rubberStampTool.getStandardStampAnnotations();

        const approvedStamp = defaultStamps.find((s) => s.getStampText() === 'Accepted');
        if (!approvedStamp) {
            console.error('Approved stamp not found');
            return;
        }
        this.multiStampModeStamp = approvedStamp;
        this.multiStampModeActive = true;

        this.instance.Core.documentViewer.setToolMode(this.rubberStampTool);
        await this.rubberStampTool.setRubberStamp(approvedStamp, '');

        this.rubberStampTool.showPreview();
    }

    public stopAddCheckAnnotations() {
        this.multiStampModeActive = false;
    }

    OpenRemark() {
        //ToDo Pause Viewer einbauen?

        this.CheckedFile$.pipe(take(1)).subscribe((file) => {
            this.matDialog
                .open<RemarkPopupComponent, RemarkPopupComponentDialogData>(RemarkPopupComponent, {
                    ...RemarkPopupComponent.DefaultConfig,
                    data: {
                        FileId: file.Id,
                    },
                })
                .afterClosed()
                .pipe(take(1));
        });
    }

    async Save() {
        if (!this.instance) return;

        const { documentViewer, annotationManager } = this.instance.Core;
        this.setReadonly(true);

        this.LS.startLoading('save-pdf-on-server-side');

        const doc = documentViewer.getDocument();
        const xfdfString = await annotationManager.exportAnnotations();
        const data = await doc.getFileData({
            xfdfString,
        });
        const arr = new Uint8Array(data);
        const blob = new Blob([arr], { type: 'application/pdf' });

        let params: FileUploadParams = new Map<keyof fileUploadParamsObj, string>([['document_id', this.Version.parentDocumentId.toString()]]);

        this.store.dispatch(
            FilesActionTypes.UploadFileBackendRequest({
                Payload: {
                    file: blob,
                    params: params,
                },
            }),
        );

        this._dirty = false;

        this.SaveButton = false;
        this.Editing = false;
        this.toastrService.success('PDF erfolgreich gespeichert');

        await this.fs.pollFilesAndLinksByVersionId(this.Version.Id);
        // todo: timeouts aus angular zone entfernen
        setTimeout(() => this.fs.pollFilesAndLinksByVersionId(this.Version.Id), 3500);
        setTimeout(() => this.fs.pollFilesAndLinksByVersionId(this.Version.Id), 10000);

        this.LS.endLoading('save-pdf-on-server-side');
    }

    canDeactivate(): boolean {
        if (!this.instance?.Core?.documentViewer) return true;
        return !this._dirty;
    }

    public async Close() {
        await this.instance.UI.dispose();
    }

    Edit() {
        this.SaveButton = true;
        this.Editing = !this.Editing;

        this.setReadonly(false);
        if (this.Editing == false) {
            this.setReadonly(true);
        }
    }
    setReadonly(readonly: boolean) {
        if (readonly) {
            // this.instance.Core.annotationManager.enableReadOnlyMode();
            this.instance.UI.enableViewOnlyMode();
        } else {
            // this.instance.Core.annotationManager.disableReadOnlyMode();
            this.instance.UI.disableViewOnlyMode();
        }
    }
    async load() {
        this.isLoading$.next(true);
        const token = await firstValueFrom(this.token$);

        // const gatewayUrl = environment.gatewayApiUrl;
        // this.instance.UI.loadDocument(`${gatewayUrl}/file/files/download/${this.loadedVersionId}?document_id=${this.Version.parentDocumentId}&token=${token}`); //GetDownloadLink liefert anderen Link ohne file
        await this.instance.UI.loadDocument(HttpService.getUrl(this.Version.GetDownloadLink(token), 'file'));
        this._dirty = false;
        if (this.SigningMode) {
            this.Edit();
            this.OpenSignaturePopup();
        }else{
            this.setReadonly(true);
        }
    }

    async initPDFViewer() {
        await this.ngZone.runOutsideAngular(async () => {
            this.instance = await WebViewer(
                {
                    path: 'assets/pdf-net/webviewer11.9.1',
                    licenseKey: environment.apryseLicenseKey,
                    css: 'assets/pdf-net/main.css',
                },
                this.viewer.nativeElement,
            );
        });
        this.setReadonly(true);
        await this.instance.UI.setLanguage('de');
        this.instance.UI.setPrintQuality(2);
        this.instance.UI.closeElements(['notesPanel']);
        this.darkModeSub = this.store
            .select(getSetting)
            .pipe(
                map((s) => s.DarkMode),
                distinctUntilChanged(),
            )
            .subscribe((isDarkTheme) => this.instance.UI.setTheme(isDarkTheme ? 'dark' : 'light'));
        this.userSub = this.store.select(getUser).subscribe((user) => {
            if (user) {
                this.instance.Core.annotationManager.setCurrentUser(user.Firstname + ' ' + user.Lastname);
                this.instance.Core.annotationManager.promoteUserToAdmin()
            }
        });

        this.rubberStampTool = this.instance.Core.documentViewer.getToolModeMap()[this.instance.Core.Tools.ToolNames.RUBBER_STAMP];
        this.rubberStampTool.addEventListener('annotationAdded', (event) => {
             console.log('Annotation added', event);
            if (this.multiStampModeActive) {
                this.instance.Core.documentViewer.setToolMode(this.rubberStampTool);
                this.rubberStampTool.setRubberStamp(this.multiStampModeStamp, '');
            }
        });

        // documentViewer.addEventListener('annotationsLoaded', () => {
        //     console.log('annotations loaded');
        // });
        //
        this.instance.Core.documentViewer.addEventListener('documentLoaded', () => {
            this.isLoading$.next(false);
        });
        this.instance.Core.annotationManager.addEventListener('annotationChanged', (annotations: Core.Annotations.Annotation[], action: string, info:  	Core.AnnotationManager.AnnotationChangedInfoObject 	) => {
           // console.log('annotationChanged', {annotations, info, action});
            if (!info.imported) {
                this._dirty = true;
            }
        });

        this.instance.Core.annotationManager.addEventListener('fieldChanged', (e) => {
            this._dirty = true;
           // console.log('fieldChanged', e);
        });


        const UIEvents = this.instance.UI.Events;
        this.instance.UI.addEventListener(UIEvents.LOAD_ERROR, (err) => {
            // Do something with error. eg. instance.showErrorMessage('An error has occurred')
            console.log('loadError', err);
            // todo pusht to sentry
            this.isLoading$.next(false);
            this.toastrService.error('PDF öffnen fehlgeschlagen', err.detail.message);
        });
    }
    async ngAfterViewInit(): Promise<void> {
        await this.initPDFViewer();
        const token = await firstValueFrom(this.token$);
        const body = { versionId: this.Version.Id };
        this.LS.startLoading('getPDFElements');

        try {
            const response = await fetch(this.ServiceGetElements, {
                method: 'Post',
                headers: {
                    Authorization: 'Bearer ' + token,
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(body),
            });
            const data = await response.json();

            const isEmpty =
                data === null || data === undefined || (typeof data === 'string' && data.trim() === '') || (Array.isArray(data) && data.length === 0) || (typeof data === 'object' && !Array.isArray(data) && Object.keys(data).length === 0);

            this.LS.endLoading('getPDFElements');

            if (isEmpty) {
                this.load();
            } else {
                this.matDialog
                    .open<PdfEditableDialogComponent, PDFEditableDialogData, PDFEditableDialogReturnData>(PdfEditableDialogComponent, {})
                    .afterClosed()
                    .subscribe(async (selectedOption) => {
                        this.LS.startLoading('upload-optimised-pdf-version');

                        if (selectedOption) {
                            const res = await this.pts.convertPDFToImage({
                                VersionId: this.Version.Id.toString(),
                                OnlyOnePage: false,
                                MaxDimension: '9000',
                            });

                            const documentIds: number[] = res.DocumentIds.map((i) => Number(i));
                            const files = await firstValueFrom(this.fs.GetFilesById(documentIds));

                            const imgVersionIds: string[] = files.map((file) => file.Versions[0].Id.toString());

                            const bodyUploadNewVersionFromImages = {
                                documentId: this.Version.parentDocumentId,
                                imgIds: imgVersionIds.join(','),
                            };

                            fetch(this.ServiceUploadNewVersionFromImages, {
                                method: 'Post',
                                headers: {
                                    Authorization: 'Bearer ' + token,
                                    'Content-Type': 'application/json',
                                },
                                body: JSON.stringify(bodyUploadNewVersionFromImages),
                            })
                                .then((response) => response.json())
                                .then(async (data) => {
                                    if (data) {
                                        this.Version = this.Version.Clone({
                                            Id: data.version.id
                                        });
                                        this.load();
                                    }
                                    this.LS.endLoading('upload-optimised-pdf-version');
                                })
                                .catch((err) => {
                                    this.toastrService.error('Fehler beim umwandeln der PDF.', 'Fehler');
                                    this.LS.endLoading('upload-optimised-pdf-version');
                                });
                        } else {
                            this.LS.endLoading('upload-optimised-pdf-version');
                        }
                    });
            }
        } catch (e) {
            console.log(e)
            this.toastrService.error('Fehler beim Laden der PDF-Elemente.', 'Fehler');
            this.LS.endLoading('getPDFElements');
        }
    }

    changeChecked() {
        this.CheckedFile$.pipe(take(1)).subscribe((file) => {
            this.store.dispatch(
                FilesActionTypes.ModifyFileRequest({
                    Payload: {
                        DocumentId: this.SetCheckedButtonDocumentId.toString(),
                        Checked: !file.Checked,
                    },
                }),
            );
        });

        this.LS.startLoading('save-pdf-on-server-side_changeChecked');

        firstValueFrom(merge(this.actions$.pipe(ofType(FilesActionTypes.ModifyFileSuccess)), this.actions$.pipe(ofType(FilesActionTypes.ModifyFileFailure)))).then((action) => {
            this.LS.endLoading('save-pdf-on-server-side_changeChecked');
            if (action.type === FilesActionTypes.ModifyFileSuccess.type) {
                this.toastrService.success('Dokumentstatus erfolgreich gespeichert');
            } else if (action.type === FilesActionTypes.ModifyFileFailure.type) {
                this.store.dispatch(
                    BaseActionTypes.ErrorAction({
                        Payload: {
                            ToasterMessage: 'Dokumentstatus speichern fehlgeschlagen',
                            Err: action.Payload.Errors,
                        },
                    }),
                );
            }
        });
    }

    ngOnDestroy() {
        this.destroy$.next();

        this.destroy$.complete();
        this.userSub?.unsubscribe();
        this.darkModeSub?.unsubscribe();
        this.Close();
    }

}
